// Author: Sunil Jain

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import logger from '../utils/logger';

const ProtectedRoute = ({ children }) => {

    const { user, isAuthenticated, isLoading, queryStatus, logout, selectedTenant } = useAuth();
    const location = useLocation();
    
    const log = (message) => {
        if (process.env.NODE_ENV === 'development') {
            (logger?.debug || console.log)(message);
        }
    };

    const rolesRequiringTraining = [
        'physician_qhp',
        'clinical_staff',
        'specialty_admin',
        'tenant_admin'
    ];

    log(`>>> PROTECTED ROUTE RENDER: Pathname=${location.pathname}`);
    log(`>>> Query Status: ${queryStatus}`);
    log(`>>> Is Loading: ${isLoading}`);
    log(`>>> Is Authenticated: ${isAuthenticated}`);
    log(`>>> User Object Exists: ${!!user}`);

    // --- Loading State Check ---
    if (isLoading) {
        log(`ProtectedRoute: Showing Loading Spinner (isLoading=${isLoading}, queryStatus=${queryStatus})`);
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                <CircularProgress />
            </Box>
        );
    }

    // --- Authentication Check ---
    if (!isAuthenticated) {
        log(`ProtectedRoute: NOT Authenticated (isLoading=${isLoading}, isAuthenticated=${isAuthenticated}). Redirecting to /login.`);
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // --- Authenticated: Check User Object ---
    if (!user) {
        (logger?.error || console.error)("ProtectedRoute CRITICAL ERROR: isAuthenticated is true but user object is null/undefined! This should not happen. Redirecting to login.");
        // If this happens, logout to be safe
        logout();
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // --- Patient Role Security Check ---
    if (user.role === 'patient') {
        (logger.error || console.error)("ProtectedRoute: Patient role detected in staff portal. Access DENIED. Forcing logout.");
        logout();
        return (
             <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                <CircularProgress />
            </Box>
        );
    }

    const userRole = selectedTenant?.role;
    const isRestrictedStaff = userRole === 'staff' || userRole === 'clinical_staff';
    if (isRestrictedStaff && 
        location.pathname !== '/dashboard' && 
        location.pathname !== '/profile' &&
        location.pathname !== '/onboarding-training' &&
        !location.pathname.startsWith('/patients/') 
    ) {
        log(`ProtectedRoute: User role (${userRole}) is restricted. Redirecting from ${location.pathname} to /dashboard.`);
        return <Navigate to="/dashboard" replace />;
    }
    const requiresTraining = rolesRequiringTraining.includes(user.role);
    const trainingNotCompleted = !user.has_completed_onboarding_training;

    console.log(`%cProtectedRoute Check (Auth=true, User=true): Path=${location.pathname}`, "color: magenta; font-weight: bold;");
    console.log(`%cProtectedRoute Check: user.role = ${user.role}`, "color: magenta;");
    console.log(`%cProtectedRoute Check: user.has_completed_onboarding_training = ${user.has_completed_onboarding_training}`, "color: magenta;");
    console.log(`%cProtectedRoute Check: requiresTraining = ${requiresTraining}`, "color: magenta;");
    console.log(`%cProtectedRoute Check: trainingNotCompleted = ${trainingNotCompleted}`, "color: magenta;");

    // --- Training Redirection Logic ---
    if (requiresTraining && trainingNotCompleted) {
        if (location.pathname !== '/onboarding-training') {
            log("ProtectedRoute: Authenticated, but training required and not on training page. Redirecting to /onboarding-training.");
            return <Navigate to="/onboarding-training" state={{ from: location }} replace />;
        } else {
            log("ProtectedRoute: Authenticated, training required, already on training page. Rendering children (OnboardingTraining page).");
            return children;
        }
    }

    log(`ProtectedRoute: Authenticated and authorized (Role: ${userRole}, Path: ${location.pathname}). Rendering children.`);
    return children;
};

export default React.memo(ProtectedRoute);